// WebSocket client for `wss://<ws>/ws/{tenant_id}?token=<JWT>`.
//
// Contract (matches services/websocket-service/app/main.py after Batch D2):
//   • server frame:   { seq:N, channel, data }
//   • server ping:    { seq:N, type:"ping" }     — every 30s
//   • client pong:    { type:"pong" }            — must reply within 10s
//   • server frames also act as keep-alive; the watchdog only fires when no
//     frame at all arrives for > _LIVENESS_TIMEOUT_MS.
//
// On disconnect, we reconnect with full-jitter exponential backoff starting at
// 1s, capped at 30s. Subscribers re-bind automatically.

import { tokenStore } from "@/lib/auth/tokenStore";
import { config } from "@/lib/config";

type Handler = (payload: unknown) => void;

interface ServerFrame {
  seq?: number;
  channel?: string;
  data?: unknown;
  type?: string;
}

const _LIVENESS_TIMEOUT_MS = 45_000;
const _MIN_BACKOFF_MS = 1_000;
const _MAX_BACKOFF_MS = 30_000;

export class WsClient {
  private socket: WebSocket | null = null;
  private subscribers = new Map<string, Set<Handler>>();
  private lastSeq: number | null = null;
  private livenessTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private closedByCaller = false;

  constructor(private readonly tenantId: number) {}

  connect(): void {
    this.closedByCaller = false;
    this._openSocket();
  }

  close(): void {
    this.closedByCaller = true;
    this._teardown();
  }

  subscribe(channel: string, handler: Handler): () => void {
    const set = this.subscribers.get(channel) ?? new Set();
    set.add(handler);
    this.subscribers.set(channel, set);
    return () => {
      const current = this.subscribers.get(channel);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) this.subscribers.delete(channel);
    };
  }

  private _openSocket(): void {
    const token = tokenStore.accessToken;
    if (!token) {
      // No point opening a socket without auth — the server closes 4001.
      return;
    }
    const base = config.wsEndpoint.replace(/\/+$/, "");
    const url = `${base}/ws/${this.tenantId}?token=${encodeURIComponent(token)}`;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("ws_open_failed", error);
      this._scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this._armLivenessTimer();
    };
    socket.onmessage = (event) => this._onMessage(event);
    socket.onclose = (event) => this._onClose(event);
    socket.onerror = () => {
      // onclose follows; just log here.
      // eslint-disable-next-line no-console
      console.warn("ws_error tenant=%s", this.tenantId);
    };
  }

  private _onMessage(event: MessageEvent): void {
    this._armLivenessTimer();
    let frame: ServerFrame;
    try {
      frame = JSON.parse(typeof event.data === "string" ? event.data : "");
    } catch {
      return;
    }
    // Detect gaps in monotonic seq.
    if (typeof frame.seq === "number") {
      if (this.lastSeq !== null && frame.seq !== this.lastSeq + 1) {
        // eslint-disable-next-line no-console
        console.warn("ws_gap_detected", { last: this.lastSeq, next: frame.seq });
      }
      this.lastSeq = frame.seq;
    }
    // Ping → pong as fast as we can. Ping frames also carry seq, so update
    // lastSeq before returning or the next data frame looks like a false gap.
    if (frame.type === "ping") {
      this._send({ type: "pong" });
      return;
    }
    if (typeof frame.channel === "string") {
      const handlers = this.subscribers.get(frame.channel);
      if (handlers) {
        for (const handler of handlers) {
          try {
            handler(frame.data);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("ws_handler_error", error);
          }
        }
      }
    }
  }

  private _onClose(event: CloseEvent): void {
    this._clearLivenessTimer();
    this.socket = null;
    // 4001 = invalid token, 4003 = tenant mismatch — neither is fixable by
    // reconnecting.
    if (event.code === 4001 || event.code === 4003 || this.closedByCaller) {
      return;
    }
    this._scheduleReconnect();
  }

  private _send(payload: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(payload));
      } catch {
        // ignore — onclose will follow if the socket is dead.
      }
    }
  }

  private _armLivenessTimer(): void {
    this._clearLivenessTimer();
    this.livenessTimer = setTimeout(() => {
      // No frame in window — assume dead and force a reconnect.
      // eslint-disable-next-line no-console
      console.warn("ws_liveness_timeout tenant=%s", this.tenantId);
      this.socket?.close();
    }, _LIVENESS_TIMEOUT_MS);
  }

  private _clearLivenessTimer(): void {
    if (this.livenessTimer) {
      clearTimeout(this.livenessTimer);
      this.livenessTimer = null;
    }
  }

  private _scheduleReconnect(): void {
    this.reconnectAttempts += 1;
    const exp = Math.min(_MAX_BACKOFF_MS, _MIN_BACKOFF_MS * 2 ** (this.reconnectAttempts - 1));
    const delay = Math.random() * exp; // full-jitter
    setTimeout(() => {
      if (!this.closedByCaller) this._openSocket();
    }, delay);
  }

  private _teardown(): void {
    this._clearLivenessTimer();
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.subscribers.clear();
    this.lastSeq = null;
    this.reconnectAttempts = 0;
  }
}
